import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mic, TrendingUp, Users, MessageSquare, Building2, UserCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const PERSONAS = [
  {
    id: "max-returns",
    name: "Max Returns",
    role: "AI Investor",
    description: "A seasoned VC who started as a scientist. He knows what it's like to face skeptical rooms and helps founders translate science into investable stories.",
    color: "from-cyan-500 to-purple-500",
    bgColor: "bg-gradient-to-br from-cyan-500/10 to-purple-500/10",
    borderColor: "border-cyan-500/30",
    icon: TrendingUp,
    image: "/max_returns.png",
    path: "/ai-persona/max-returns"
  },
  {
    id: "diana-pipeline",
    name: "Diana Pipeline",
    role: "AI Pharma Director",
    description: "Head of External Innovation at a top-10 pharma company. She evaluates strategic fit, development pathways, and partnership potential.",
    color: "from-emerald-500 to-teal-500",
    bgColor: "bg-gradient-to-br from-emerald-500/10 to-teal-500/10",
    borderColor: "border-emerald-500/30",
    icon: Building2,
    image: "/diana_pipeline.png",
    path: "/ai-persona/diana-pipeline"
  },
  {
    id: "marco-moderator",
    name: "Sergej Panél",
    role: "AI Panel Moderator",
    description: "The charismatic host who keeps the conversation flowing. He introduces speakers, manages Q&A, and ensures productive discussions.",
    color: "from-amber-500 to-orange-500",
    bgColor: "bg-gradient-to-br from-amber-500/10 to-orange-500/10",
    borderColor: "border-amber-500/30",
    icon: Users,
    image: "/marco_moderator.png",
    path: "/ai-persona/marco-moderator"
  },
  {
    id: "alex-attendee",
    name: "Alex Chen",
    role: "AI Attendee",
    description: "A curious conference attendee - could be a fellow founder, scientist, or industry observer. Great for practice pitches and networking conversations.",
    color: "from-rose-500 to-pink-500",
    bgColor: "bg-gradient-to-br from-rose-500/10 to-pink-500/10",
    borderColor: "border-rose-500/30",
    icon: UserCircle,
    image: "/alex_chen.png",
    path: "/ai-persona/alex-attendee"
  }
];

export default function AIPersonaSelector() {
  const navigate = useNavigate();
  const [hoveredPersona, setHoveredPersona] = useState(null);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800/50 bg-slate-950/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/")} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <Mic className="w-5 h-5 text-purple-400" />
            <span className="font-semibold">AI Voice Personas</span>
          </div>
          <div className="w-20" />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            Choose Your AI Conversation Partner
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Practice your pitch, get feedback, or simulate conference interactions with our AI personas. 
            Each character brings a unique perspective to help you prepare.
          </p>
        </div>

        {/* Persona Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {PERSONAS.map((persona) => {
            const Icon = persona.icon;
            const isHovered = hoveredPersona === persona.id;
            
            return (
              <Card 
                key={persona.id}
                className={`${persona.bgColor} border ${persona.borderColor} cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-xl ${isHovered ? 'ring-2 ring-white/20' : ''}`}
                onClick={() => navigate(persona.path)}
                onMouseEnter={() => setHoveredPersona(persona.id)}
                onMouseLeave={() => setHoveredPersona(null)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${persona.color} overflow-hidden shadow-lg flex-shrink-0`}>
                      <img 
                        src={persona.image} 
                        alt={persona.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-xl font-bold text-white">{persona.name}</h3>
                        <Icon className="w-4 h-4 text-slate-400" />
                      </div>
                      <p className={`text-sm font-medium bg-gradient-to-r ${persona.color} bg-clip-text text-transparent mb-2`}>
                        {persona.role}
                      </p>
                      <p className="text-slate-400 text-sm leading-relaxed">
                        {persona.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Action hint */}
                  <div className={`mt-4 pt-4 border-t border-slate-700/50 flex items-center justify-between transition-opacity ${isHovered ? 'opacity-100' : 'opacity-60'}`}>
                    <span className="text-xs text-slate-500">Click to start conversation</span>
                    <Button 
                      size="sm" 
                      className={`bg-gradient-to-r ${persona.color} text-white text-xs px-4`}
                    >
                      <Mic className="w-3 h-3 mr-1" /> Start
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info Section */}
        <div className="mt-12 text-center">
          <p className="text-slate-500 text-sm">
            All conversations use OpenAI's Realtime Voice API. Requires microphone access.
          </p>
        </div>
      </main>
    </div>
  );
}
